import { Suspense, lazy, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicOnlyRoute } from './PublicOnlyRoute';
import { HomeRouter } from './HomeRouter';
import { GroupsRouter } from './GroupsRouter';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { PageSpinner } from '@/components/ui/page-spinner';
import { RootLayout } from './RootLayout';
import { GlobalRouteErrorBoundary } from './GlobalRouteErrorBoundary';
import { FeatureGateRoute } from './FeatureGateRoute';
import { FeatureFlags, type FeatureFlagKey } from '@/lib/feature-flag-constants';
import { routePatterns } from './paths';
import { ProfileLegacyRedirect } from './ProfileLegacyRedirect';

type RouteComponent = ComponentType;

const lazyRoute = (
    loader: () => Promise<Record<string, unknown>>,
    exportName: string,
) =>
    lazy(async () => {
        const module = await loader();
        const component = module[exportName];

        if (typeof component !== 'function') {
            throw new Error(`Route export "${exportName}" is not a React component.`);
        }

        return { default: component as RouteComponent };
    });

const routeElement = (element: ReactElement) => (
    <Suspense fallback={<PageSpinner />}>
        {element}
    </Suspense>
);

const fullScreenRouteElement = (element: ReactElement) => (
    <Suspense fallback={<LoadingScreen />}>
        {element}
    </Suspense>
);

const gateScreen = (flag: FeatureFlagKey, element: JSX.Element) => (
    <FeatureGateRoute flag={flag}>{element}</FeatureGateRoute>
);

const gatedRouteElement = (flag: FeatureFlagKey, element: ReactElement) =>
    gateScreen(flag, routeElement(element));


const loadAuthScreens = () => import('@/screens/auth');
const loadHomeScreens = () => import('@/screens/home');
const loadOnboardingScreens = () => import('@/screens/onboarding');
const loadExpensesScreens = () => import('@/screens/expenses');
const loadDocumentsScreens = () => import('@/screens/documents');
const loadScheduleScreens = () => import('@/screens/schedule');
const loadIntakeScreens = () => import('@/screens/intake');
const loadHomeSearchScreens = () => import('@/screens/home-search');
const loadBillingScreens = () => import('@/screens/billing');
const loadActivityScreens = () => import('@/screens/activity');

const loadNotFoundScreen = () => import('@/screens/NotFound');

const SignIn = lazyRoute(loadAuthScreens, 'SignIn');
const SignUp = lazyRoute(loadAuthScreens, 'SignUp');
const VerifyEmail = lazyRoute(loadAuthScreens, 'VerifyEmail');
const EmailConfirmation = lazyRoute(loadAuthScreens, 'EmailConfirmation');
const ForgotPassword = lazyRoute(loadAuthScreens, 'ForgotPassword');
const ResetPassword = lazyRoute(loadAuthScreens, 'ResetPassword');

const HomeHub = lazyRoute(loadHomeScreens, 'HomeHub');
const HomePreferences = lazyRoute(loadHomeScreens, 'HomePreferences');
const HomePreferencesHome = lazyRoute(loadHomeScreens, 'HomePreferencesHome');
const HomePreferencesGeneral = lazyRoute(loadHomeScreens, 'HomePreferencesGeneral');
const HomePreferencesMembers = lazyRoute(loadHomeScreens, 'HomePreferencesMembers');
const HomePreferencesBooking = lazyRoute(loadHomeScreens, 'HomePreferencesBooking');
const HomePreferencesFinance = lazyRoute(loadHomeScreens, 'HomePreferencesFinance');
const HomePreferencesCategories = lazyRoute(loadHomeScreens, 'HomePreferencesCategories');
const HomePreferencesIntegrations = lazyRoute(loadHomeScreens, 'HomePreferencesIntegrations');
const HomeInvites = lazyRoute(loadHomeScreens, 'HomeInvites');
const PublicHomeInviteAccept = lazyRoute(loadHomeScreens, 'PublicHomeInviteAccept');

const OnboardingFlow = lazyRoute(loadOnboardingScreens, 'OnboardingFlow');

const ExpenseManagement = lazyRoute(loadExpensesScreens, 'ExpenseManagement');
const BudgetManagement = lazyRoute(loadExpensesScreens, 'BudgetManagement');
const BankReconciliation = lazyRoute(loadExpensesScreens, 'BankReconciliation');
const ExpenseDetails = lazyRoute(loadExpensesScreens, 'ExpenseDetails');
const ExpenseForm = lazyRoute(loadExpensesScreens, 'ExpenseForm');
const AssetForm = lazyRoute(loadExpensesScreens, 'AssetForm');
const AssetDetails = lazyRoute(loadExpensesScreens, 'AssetDetails');
const DocumentVault = lazyRoute(loadDocumentsScreens, 'DocumentVault');
const DocumentDetails = lazyRoute(loadDocumentsScreens, 'DocumentDetails');
const DocumentForm = lazyRoute(loadDocumentsScreens, 'DocumentForm');
const ScheduleScreen = lazyRoute(loadScheduleScreens, 'ScheduleScreen');
const IntakeReview = lazyRoute(loadIntakeScreens, 'IntakeReview');
const ProcessingLog = lazyRoute(loadIntakeScreens, 'ProcessingLog');

const GroupHub = lazyRoute(loadHomeSearchScreens, 'GroupHub');
const GroupListings = lazyRoute(loadHomeSearchScreens, 'GroupListings');
const HomeSearchInvites = lazyRoute(loadHomeSearchScreens, 'HomeSearchInvites');
const ListingDetails = lazyRoute(loadHomeSearchScreens, 'ListingDetails');
const ListingForm = lazyRoute(loadHomeSearchScreens, 'ListingForm');
const HomeSearchPreferences = lazyRoute(loadHomeSearchScreens, 'HomeSearchPreferences');
const HomeSearchPreferencesHome = lazyRoute(loadHomeSearchScreens, 'HomeSearchPreferencesHome');
const HomeSearchPreferencesDetails = lazyRoute(loadHomeSearchScreens, 'HomeSearchPreferencesDetails');
const HomeSearchPreferencesMembers = lazyRoute(loadHomeSearchScreens, 'HomeSearchPreferencesMembers');
const HomeSearchPreferencesMine = lazyRoute(loadHomeSearchScreens, 'HomeSearchPreferencesMine');
const PublicInviteAccept = lazyRoute(loadHomeSearchScreens, 'PublicInviteAccept');

const GroupBilling = lazyRoute(loadBillingScreens, 'GroupBilling');
const HomeBilling = lazyRoute(loadBillingScreens, 'HomeBilling');

// One section for both stages — the scope comes from the route. Activity
// replaced the Inbox and the notification centre, which had asked the same
// question twice from two different places.
const ActivityScreen = lazyRoute(loadActivityScreens, 'ActivityScreen');
const ActivityDesk = lazyRoute(loadActivityScreens, 'ActivityDesk');
const NotFound = lazyRoute(loadNotFoundScreen, 'NotFound');

/**
 * Application Router Configuration
 * 
 * Uses Data Router (createBrowserRouter) for v7 best practices.
 * organized by feature areas:
 * - Public/Auth Routes
 * - Onboarding Routes
 * - Main App Routes (Protected)
 * - Home Context Routes (Protected with specific layout)
 * 
 * Page chrome pattern:
 * Sections come from the app shell's sidebar, and each page declares its own
 * subheader (title, meta, actions) with usePageChrome. Breadcrumbs are
 * derived from the route — pass trailingCrumbs only on depth-3 record pages.
 *
 * @example
 * ```tsx
 * export function MyPage() {
 *   usePageChrome(() => ({
 *     title: 'My Page',
 *     meta: 'Description',
 *     primaryAction: <Button onClick={open}>Create</Button>,
 *   }), [open]);
 *   return <div>Content</div>;
 * }
 * ```
 */
export const router = createBrowserRouter(
    createRoutesFromElements(
        <Route element={<RootLayout />} errorElement={<GlobalRouteErrorBoundary />}>
            {/* --- Public / Auth Routes --- */}
            <Route element={<PublicOnlyRoute />}>
                <Route path={routePatterns.signIn} element={fullScreenRouteElement(<SignIn />)} />
                <Route path={routePatterns.signUp} element={fullScreenRouteElement(<SignUp />)} />
                <Route path={routePatterns.forgotPassword} element={fullScreenRouteElement(<ForgotPassword />)} />
                <Route path={routePatterns.resetPassword} element={fullScreenRouteElement(<ResetPassword />)} />
            </Route>
            <Route path={routePatterns.emailConfirmation} element={fullScreenRouteElement(<EmailConfirmation />)} />
            <Route path={routePatterns.verifyEmail} element={fullScreenRouteElement(<VerifyEmail />)} />
            <Route path={routePatterns.groupInviteAccept} element={fullScreenRouteElement(<PublicInviteAccept />)} />
            <Route path={routePatterns.homeInviteAccept} element={fullScreenRouteElement(<PublicHomeInviteAccept />)} />

            {/* --- Redesigned onboarding (single full-screen entry — own chrome, no app layout) --- */}
            <Route element={<ProtectedRoute requireHomes={false} bare />}>
                <Route path={routePatterns.onboarding} element={fullScreenRouteElement(<OnboardingFlow />)} />
            </Route>

            {/* --- Global App Routes (Protected) --- */}
            <Route element={<ProtectedRoute />}>
                <Route path={routePatterns.home} element={gateScreen(FeatureFlags.HOME_ROUTER, <HomeRouter />)} />
                <Route path={routePatterns.groups} element={gatedRouteElement(FeatureFlags.GROUP_DETAILS, <GroupsRouter />)} />
                <Route path={routePatterns.groupInvites} element={gatedRouteElement(FeatureFlags.GROUP_INVITES, <HomeSearchInvites />)} />
                <Route path={routePatterns.homeInvites} element={gatedRouteElement(FeatureFlags.HOME_INVITES, <HomeInvites />)} />
                <Route path={routePatterns.profile} element={gatedRouteElement(FeatureFlags.PROFILE_LAYOUT, <ProfileLegacyRedirect pane="general" />)} />
                <Route path={routePatterns.profileAccount} element={gatedRouteElement(FeatureFlags.PROFILE_ACCOUNT, <ProfileLegacyRedirect pane="general" />)} />
                <Route path={routePatterns.profileHomes} element={gatedRouteElement(FeatureFlags.PROFILE_HOMES, <ProfileLegacyRedirect pane="homes" />)} />
                <Route path={routePatterns.profileNotifications} element={gatedRouteElement(FeatureFlags.PROFILE_NOTIFICATIONS, <ProfileLegacyRedirect pane="notifications" />)} />
                <Route path={routePatterns.profileCalendar} element={gatedRouteElement(FeatureFlags.PROFILE_CALENDAR, <ProfileLegacyRedirect pane="calendar" />)} />

                {/* --- Group Context Routes --- */}
                {/* Sections live in the app shell's sidebar, not in header tabs. */}
                <Route path={routePatterns.group} >
                    <Route index element={gatedRouteElement(FeatureFlags.GROUP_DETAILS, <GroupHub />)} />
                    <Route path={routePatterns.groupListings} element={gatedRouteElement(FeatureFlags.GROUP_DETAILS, <GroupListings />)} />
                    <Route path={routePatterns.groupPreferences} element={gatedRouteElement(FeatureFlags.GROUP_PREFERENCES, <HomeSearchPreferences />)}>
                        <Route index element={routeElement(<HomeSearchPreferencesHome />)} />
                        <Route path={routePatterns.groupPreferencesDetails} element={gatedRouteElement(FeatureFlags.GROUP_PREFERENCES_DETAILS, <HomeSearchPreferencesDetails />)} />
                        <Route path={routePatterns.groupPreferencesMine} element={gatedRouteElement(FeatureFlags.GROUP_PREFERENCES_MINE, <HomeSearchPreferencesMine />)} />
                        <Route path={routePatterns.groupPreferencesMembers} element={gatedRouteElement(FeatureFlags.GROUP_PREFERENCES_MEMBERS, <HomeSearchPreferencesMembers />)} />
                        <Route path={routePatterns.groupBilling} element={gatedRouteElement(FeatureFlags.GROUP_BILLING, <GroupBilling />)} />
                    </Route>
                    {/* The itemId form is what the held-item email deep-links to. */}
                    <Route path={routePatterns.groupActivity} element={gatedRouteElement(FeatureFlags.GROUP_ACTIVITY, <ActivityScreen />)} />
                    <Route path={routePatterns.groupActivityReviewIndex} element={gatedRouteElement(FeatureFlags.GROUP_ACTIVITY, <ActivityDesk />)} />
                    <Route path={routePatterns.groupActivityReview} element={gatedRouteElement(FeatureFlags.GROUP_ACTIVITY, <ActivityDesk />)} />
                    <Route path={routePatterns.groupListingNew} element={gatedRouteElement(FeatureFlags.GROUP_DETAILS, <ListingForm />)} />
                    <Route path={routePatterns.groupListing} element={gatedRouteElement(FeatureFlags.GROUP_LISTING_DETAILS, <ListingDetails />)} />
                    <Route path={routePatterns.groupListingEdit} element={gatedRouteElement(FeatureFlags.GROUP_LISTING_DETAILS, <ListingForm />)} />
                </Route>

                {/* --- Home Context Routes --- */}
                <Route path={routePatterns.homeWorkspace}>
                    <Route index element={gatedRouteElement(FeatureFlags.HOME_OVERVIEW, <HomeHub />)} />
                    <Route path={routePatterns.homePreferences} element={gatedRouteElement(FeatureFlags.HOME_PREFERENCES, <HomePreferences />)}>
                        <Route index element={routeElement(<HomePreferencesHome />)} />
                        <Route path={routePatterns.homePreferencesGeneral} element={gatedRouteElement(FeatureFlags.HOME_PREFERENCES_GENERAL, <HomePreferencesGeneral />)} />
                        <Route path={routePatterns.homePreferencesLocation} element={<Navigate to="../general" replace />} />
                        <Route path={routePatterns.homePreferencesProperty} element={<Navigate to="../general" replace />} />
                        <Route path={routePatterns.homePreferencesMembers} element={gatedRouteElement(FeatureFlags.HOME_PREFERENCES_MEMBERS, <HomePreferencesMembers />)} />
                        <Route path={routePatterns.homePreferencesBooking} element={gatedRouteElement(FeatureFlags.HOME_PREFERENCES_BOOKING, <HomePreferencesBooking />)} />
                        <Route path={routePatterns.homePreferencesFinance} element={gatedRouteElement(FeatureFlags.HOME_PREFERENCES_FINANCE, <HomePreferencesFinance />)} />
                        <Route path={routePatterns.homePreferencesCategories} element={routeElement(<HomePreferencesCategories />)} />
                        <Route path={routePatterns.homePreferencesIntegrations} element={gatedRouteElement(FeatureFlags.HOME_PREFERENCES_INTEGRATIONS, <HomePreferencesIntegrations />)} />
                        <Route path={routePatterns.homeBilling} element={gatedRouteElement(FeatureFlags.HOME_BILLING, <HomeBilling />)} />
                    </Route>
                    {/* The itemId form is what the held-item email deep-links to. */}
                    <Route path={routePatterns.homeActivity} element={gatedRouteElement(FeatureFlags.HOME_ACTIVITY, <ActivityScreen />)} />
                    <Route path={routePatterns.homeActivityReviewIndex} element={gatedRouteElement(FeatureFlags.HOME_ACTIVITY, <ActivityDesk />)} />
                    <Route path={routePatterns.homeActivityReview} element={gatedRouteElement(FeatureFlags.HOME_ACTIVITY, <ActivityDesk />)} />
                    <Route path={routePatterns.homeActivityProcessing} element={gatedRouteElement(FeatureFlags.HOME_ACTIVITY, <ProcessingLog />)} />
                    <Route path={routePatterns.homeExpenses} element={gatedRouteElement(FeatureFlags.HOME_EXPENSES, <ExpenseManagement />)} />
                    <Route path={routePatterns.homeBudget} element={gatedRouteElement(FeatureFlags.HOME_EXPENSES, <BudgetManagement />)} />
                    <Route path={routePatterns.homeExpenseNew} element={gatedRouteElement(FeatureFlags.HOME_EXPENSES, <ExpenseForm />)} />
                    <Route path={routePatterns.homeBankReconciliation} element={gatedRouteElement(FeatureFlags.HOME_EXPENSES, <BankReconciliation />)} />
                    <Route path={routePatterns.homeExpense} element={gatedRouteElement(FeatureFlags.HOME_EXPENSES, <ExpenseDetails />)} />
                    <Route path={routePatterns.homeExpenseEdit} element={gatedRouteElement(FeatureFlags.HOME_EXPENSES, <ExpenseForm />)} />
                    <Route path={routePatterns.homeAssetNew} element={gatedRouteElement(FeatureFlags.HOME_ASSETS, <AssetForm />)} />
                    <Route path={routePatterns.homeAsset} element={gatedRouteElement(FeatureFlags.HOME_ASSETS, <AssetDetails />)} />
                    <Route path={routePatterns.homeAssetEdit} element={gatedRouteElement(FeatureFlags.HOME_ASSETS, <AssetForm />)} />
                    <Route path={routePatterns.homeDocuments} element={gatedRouteElement(FeatureFlags.HOME_DOCUMENTS, <DocumentVault />)} />
                    <Route path={routePatterns.homeDocumentNew} element={gatedRouteElement(FeatureFlags.HOME_DOCUMENTS, <DocumentForm />)} />
                    <Route path={routePatterns.homeDocument} element={gatedRouteElement(FeatureFlags.HOME_DOCUMENTS, <DocumentDetails />)} />
                    <Route path={routePatterns.homeDocumentEdit} element={gatedRouteElement(FeatureFlags.HOME_DOCUMENTS, <DocumentForm />)} />
                    <Route path={routePatterns.homeIntake} element={routeElement(<IntakeReview />)} />
                    <Route path={routePatterns.homeSchedule} element={gatedRouteElement(FeatureFlags.HOME_SCHEDULE, <ScheduleScreen />)} />
                </Route>
            </Route>

            {/* --- Fallbacks --- */}
            <Route path="*" element={routeElement(<NotFound />)} />
        </Route >
    )
);
